import { useQuery } from '@tanstack/react-query';
import { Card, Grid, Text, Title } from '@tremor/react';
import { RefreshCw, TriangleAlert } from 'lucide-react';
import { extractErrorMessage, getMaxHealth } from '@/lib/api';
import type { MaxHealthResponse, Retencao } from '@/pages/MaxHealthPage';

// [MAX_AB 27/10] A/B do canário MAX v1 (teste × controle por usuário novo), lido
// do ab_analysis de GET /max-health. Substituiu em 27/10 o painel do Max antigo
// (nivel_calculo='verificado', GET /max-ab). Reconstruído em 29/10 a partir do
// bundle index-DDB4VyWI.js: o fonte original (Codex) nunca foi commitado.

const inteiro = new Intl.NumberFormat('pt-BR');
const dec = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 });
const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

function pct(v: number | null | undefined) {
  return v == null ? '—' : `${dec.format(v)}%`;
}
function reais(v: number | null | undefined) {
  return v == null ? '—' : brl.format(v);
}
function usd(v: number | null | undefined) {
  return v == null ? '—' : `US$ ${v.toFixed(4)}`;
}
function delta(v: number | null | undefined, sufixo = '') {
  return v == null ? '—' : `${v > 0 ? '+' : ''}${dec.format(v)}${sufixo}`;
}
function retencao(r: Retencao) {
  return r.mature_activated_users ? `${pct(r.rate_pct)} (${r.retained}/${r.mature_activated_users})` : '— (0 maduros)';
}
function leituraRetencao(r: Retencao) {
  if (!r.mature_activated_users) return 'ainda sem usuários ativados com janela completa.';
  const ic = r.ci95 ? `IC95 ${pct(r.ci95.low_pct)}–${pct(r.ci95.high_pct)}` : 'sem IC';
  return `${ic}; ${r.sample_status === 'measured' ? 'amostra medida' : 'amostra insuficiente'}.`;
}
function diff(a: number | null, b: number | null, fmt: (v: number) => string) {
  return a == null || b == null ? '—' : fmt(b - a);
}

function Kpi({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <Card>
      <Text>{label}</Text>
      <div className="mt-2 text-2xl font-semibold text-slate-100">{value}</div>
      <div className="mt-1 text-xs text-slate-400">{hint}</div>
    </Card>
  );
}

interface Linha {
  label: string;
  control: string;
  test: string;
  delta: string;
  note: string;
  warning?: boolean;
}

export function MaxAbPage() {
  const q = useQuery({
    queryKey: ['max-ab-current-canary'],
    queryFn: async () => (await getMaxHealth(90)).data as MaxHealthResponse,
    refetchInterval: 300_000,
  });
  const d = q.data;
  const erro = q.error
    ? extractErrorMessage(q.error, 'erro ao carregar A/B MAX')
    : d && !d.disponivel
      ? d.erro ?? 'dados indisponíveis'
      : null;

  if (q.isLoading) return <div className="p-6 text-sm text-slate-400">Carregando A/B MAX…</div>;
  if (!d || !d.ab_analysis || erro)
    return (
      <Card className="border border-red-800/50 bg-red-950/20">
        <Title>A/B MAX indisponível</Title>
        <Text className="mt-2 text-red-300">{erro ?? 'A API ainda não retornou a análise do canário.'}</Text>
      </Card>
    );

  const ab = d.ab_analysis;
  const c = ab.control;
  const t = ab.test;
  const ativosC = c.users ? (100 * c.active_users) / c.users : null;
  const ativosT = t.users ? (100 * t.active_users) / t.users : null;
  const margemIncompleta = c.margin_status !== 'complete' || t.margin_status !== 'complete';
  const pp = (v: number) => delta(v, ' pp');

  const linhas: Linha[] = [
    {
      label: 'Usuários atribuídos',
      control: inteiro.format(c.users),
      test: inteiro.format(t.users),
      delta: delta(t.users - c.users),
      note: 'Unidade de randomização do rollout.',
    },
    {
      label: 'Usuários ativos',
      control: `${c.active_users} · ${pct(ativosC)}`,
      test: `${t.active_users} · ${pct(ativosT)}`,
      delta: diff(ativosC, ativosT, pp),
      note: 'Enviaram pelo menos um caso desde o cadastro.',
    },
    {
      label: 'Casos rodados',
      control: inteiro.format(c.cases_run),
      test: inteiro.format(t.cases_run),
      delta: delta(t.cases_run - c.cases_run),
      note: 'Inclui Padrão e MAX no teste; todos os casos no controle.',
    },
    {
      label: 'Mix Padrão / MAX',
      control: `${c.standard_cases} / ${c.max_cases}`,
      test: `${t.standard_cases} / ${t.max_cases} · ${pct(t.max_case_share_pct)} MAX`,
      delta: `${delta(t.max_cases - c.max_cases)} MAX`,
      note: 'Atribuição ao teste dá acesso ao MAX; o usuário ainda pode escolher Padrão.',
    },
    {
      label: 'Casos por usuário ativo',
      control: c.cases_per_active_user == null ? '—' : dec.format(c.cases_per_active_user),
      test: t.cases_per_active_user == null ? '—' : dec.format(t.cases_per_active_user),
      delta: diff(c.cases_per_active_user, t.cases_per_active_user, (v) => delta(v)),
      note: 'Intensidade de uso entre quem chegou a usar.',
    },
    {
      label: 'Concluídos',
      control: `${c.completed} · ${pct(c.completion_rate_pct)}`,
      test: `${t.completed} · ${pct(t.completion_rate_pct)}`,
      delta: diff(c.completion_rate_pct, t.completion_rate_pct, pp),
      note: 'Taxa sobre casos terminais, sem contar em andamento.',
    },
    {
      label: 'Erros de execução',
      control: `${c.errors} · ${pct(c.error_rate_pct)}`,
      test: `${t.errors} · ${pct(t.error_rate_pct)}`,
      delta: diff(c.error_rate_pct, t.error_rate_pct, pp),
      note: ab.definitions.errors,
      warning: t.errors > 0,
    },
    {
      label: 'Erros bloqueantes',
      control: `${c.blocking_errors} · ${pct(c.blocking_error_rate_pct)}`,
      test: `${t.blocking_errors} · ${pct(t.blocking_error_rate_pct)}`,
      delta: diff(c.blocking_error_rate_pct, t.blocking_error_rate_pct, pp),
      note: ab.definitions.blocking_errors,
      warning: t.blocking_errors > 0,
    },
    {
      label: 'Retenção D7',
      control: retencao(c.retention_7d),
      test: retencao(t.retention_7d),
      delta: diff(c.retention_7d.rate_pct, t.retention_7d.rate_pct, pp),
      note: `${ab.definitions.retention_7d} Controle: ${leituraRetencao(c.retention_7d)} Teste: ${leituraRetencao(t.retention_7d)}`,
    },
    {
      label: 'Retenção D28',
      control: retencao(c.retention_28d),
      test: retencao(t.retention_28d),
      delta: diff(c.retention_28d.rate_pct, t.retention_28d.rate_pct, pp),
      note: `${ab.definitions.retention_28d} Controle: ${leituraRetencao(c.retention_28d)} Teste: ${leituraRetencao(t.retention_28d)}`,
    },
    {
      label: 'Valor gasto / receita',
      control: `${reais(c.customer_spend_brl)} · ${c.purchases} compras`,
      test: `${reais(t.customer_spend_brl)} · ${t.purchases} compras`,
      delta: reais(t.customer_spend_brl - c.customer_spend_brl),
      note: ab.definitions.spend,
    },
    {
      label: 'Receita por usuário',
      control: reais(c.revenue_per_user_brl),
      test: reais(t.revenue_per_user_brl),
      delta: diff(c.revenue_per_user_brl, t.revenue_per_user_brl, reais),
      note: 'Inclui usuários sem compra no denominador.',
    },
    {
      label: 'Custo de tokens',
      control: `${usd(c.token_cost_usd)} · ${reais(c.token_cost_brl)}`,
      test: `${usd(t.token_cost_usd)} · ${reais(t.token_cost_brl)}`,
      delta: usd(t.token_cost_usd - c.token_cost_usd),
      note: `${ab.definitions.token_cost} USD/BRL ${ab.usd_brl_rate.toFixed(2)}.`,
    },
    {
      label: 'Tokens input / output',
      control: `${inteiro.format(c.input_tokens)} / ${inteiro.format(c.output_tokens)}`,
      test: `${inteiro.format(t.input_tokens)} / ${inteiro.format(t.output_tokens)}`,
      delta: `${delta(t.input_tokens - c.input_tokens)} / ${delta(t.output_tokens - c.output_tokens)}`,
      note: 'Volume medido no processo; custo monetário é a métrica econômica principal.',
    },
    {
      label: 'Cobertura do custo',
      control: `${pct(c.cost_coverage_pct)} (${c.cost_cases}/${c.cases_run})`,
      test: `${pct(t.cost_coverage_pct)} (${t.cost_cases}/${t.cases_run})`,
      delta: diff(c.cost_coverage_pct, t.cost_coverage_pct, pp),
      note: 'Sem 100%, custo está subestimado e margem está superestimada.',
      warning: margemIncompleta,
    },
    {
      label: 'Custo por caso instrumentado',
      control: usd(c.cost_per_case_usd),
      test: usd(t.cost_per_case_usd),
      delta: diff(c.cost_per_case_usd, t.cost_per_case_usd, usd),
      note: 'Média apenas entre casos com ai_cost_usd persistido.',
    },
    {
      label: 'Margem observada',
      control: `${reais(c.observed_margin_brl)} · ${pct(c.observed_margin_pct)}`,
      test: `${reais(t.observed_margin_brl)} · ${pct(t.observed_margin_pct)}`,
      delta: reais(t.observed_margin_brl - c.observed_margin_brl),
      note: ab.definitions.margin,
      warning: margemIncompleta,
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Title>A/B MAX · canário atual</Title>
          <Text>
            Teste de {d.rollout.target_pct}% dos novos usuários · MAX = 5 créditos · release {d.rollout.release_id} ·
            início {new Date(d.window.cutoff).toLocaleString('pt-BR')} · unidade: usuário
          </Text>
        </div>
        <button
          type="button"
          onClick={() => void q.refetch()}
          className="rounded-md border border-slate-700 p-2 text-slate-300 hover:bg-slate-800"
          title="Atualizar"
        >
          <RefreshCw className={`h-4 w-4 ${q.isFetching ? 'animate-spin' : ''}`} />
        </button>
      </header>

      {ab.sample_status === 'insufficient_sample' && (
        <Card className="border border-amber-800/60 bg-amber-950/30">
          <div className="flex gap-3 text-amber-200">
            <TriangleAlert className="h-5 w-5 shrink-0" />
            <div>
              <div className="font-semibold">Amostra ainda insuficiente para decisão</div>
              <div className="mt-1 text-sm">
                Os números abaixo são telemetria operacional, não evidência causal conclusiva. Retenção só aparece
                quando o denominador amadurece.
              </div>
            </div>
          </div>
        </Card>
      )}

      <Grid numItems={1} numItemsSm={4} className="gap-4">
        <Kpi
          label="Controle"
          value={inteiro.format(c.users)}
          hint={`${c.active_users} usuários ativos · ${c.cases_run} casos`}
        />
        <Kpi
          label="Teste MAX"
          value={inteiro.format(t.users)}
          hint={`${t.active_users} usuários ativos · ${t.cases_run} casos`}
        />
        <Kpi
          label="Atribuição observada"
          value={pct(d.rollout.actual_assignment_pct)}
          hint={`Alvo ${d.rollout.target_pct}% · ${
            d.rollout.assignment_ci95
              ? `IC95 ${pct(d.rollout.assignment_ci95.low_pct)}–${pct(d.rollout.assignment_ci95.high_pct)}`
              : 'sem IC'
          }`}
        />
        <Kpi
          label="Margem teste"
          value={reais(t.observed_margin_brl)}
          hint={`${pct(t.observed_margin_pct)} · ${t.margin_status === 'complete' ? 'custo completo' : 'limite superior; faltam custos'}`}
        />
      </Grid>

      <section className="space-y-3">
        <div>
          <Title>Análise completa: teste × controle</Title>
          <Text>
            Δ = teste menos controle. Contagens e taxas são exibidas juntas para evitar leitura enganosa de amostras
            pequenas.
          </Text>
        </div>
        <div className="overflow-x-auto rounded-lg border border-slate-800">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-900/70 text-left text-xs uppercase text-slate-400">
              <tr>
                <th className="px-4 py-3">Métrica</th>
                <th className="px-4 py-3 text-right">{ab.control_label}</th>
                <th className="px-4 py-3 text-right">{ab.test_label}</th>
                <th className="px-4 py-3 text-right">Δ</th>
                <th className="px-4 py-3">Leitura</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {linhas.map((l) => (
                <tr key={l.label} className={l.warning ? 'bg-amber-950/10' : ''}>
                  <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-200">{l.label}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">{l.control}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-medium tabular-nums text-blue-200">{l.test}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">{l.delta}</td>
                  <td className="min-w-[300px] px-4 py-3 text-xs text-slate-400">{l.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Grid numItems={1} numItemsLg={2} className="gap-4">
        <Card>
          <div className="font-medium text-slate-200">Atribuição de receita e contaminação</div>
          <div className="mt-3 space-y-2 text-sm text-slate-300">
            <div>
              Compras atribuídas diretamente:{' '}
              <b>
                {ab.revenue_attribution.direct_purchases}/{ab.revenue_attribution.purchases_in_experiment_orgs}
              </b>{' '}
              · {pct(ab.revenue_attribution.coverage_pct)}
            </div>
            <div>
              Organizações presentes nos dois braços: <b>{ab.revenue_attribution.shared_orgs_across_arms}</b>
            </div>
            <div className="text-xs text-slate-400">{ab.revenue_attribution.note}</div>
          </div>
        </Card>
        <Card>
          <div className="font-medium text-slate-200">Status dos casos</div>
          <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
            {([[ab.control_label, c.status_counts], [ab.test_label, t.status_counts]] as const).map(([rotulo, contagens]) => (
              <div key={rotulo}>
                <div className="mb-2 text-xs font-semibold uppercase text-slate-400">{rotulo}</div>
                {Object.entries(contagens).map(([status, n]) => (
                  <div key={status} className="flex justify-between gap-3 border-b border-slate-800 py-1 last:border-0">
                    <span className="text-slate-400">{status}</span>
                    <span>{n}</span>
                  </div>
                ))}
                {Object.keys(contagens).length === 0 && <div className="text-slate-500">Sem casos.</div>}
              </div>
            ))}
          </div>
        </Card>
      </Grid>

      <Card>
        <div className="font-medium text-slate-200">Definições fixas desta leitura</div>
        <div className="mt-3 grid gap-3 text-sm md:grid-cols-2">
          {Object.entries(ab.definitions).map(([chave, texto]) => (
            <div key={chave} className="rounded border border-slate-800 bg-slate-950/30 p-3">
              <div className="text-xs font-semibold uppercase text-slate-500">{chave.replace(/_/g, ' ')}</div>
              <div className="mt-1 text-slate-300">{texto}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
